/**
 * What this tab already knows, kept between screens (Sep 11 2026).
 *
 * Thomas: "why do we need a loading screen every time we click between
 * Settings, Billing, Team ... seems like that should be smoother." Every
 * signed-in screen asks the Worker who is signed in, then reads five sources
 * on mount, and each screen drew the splash while it waited. The reads have
 * not changed. What changed is that a screen starts from the last answer this
 * tab had and replaces it when the new one lands.
 *
 * The rules that keep that honest:
 *   - MEMORY ONLY. Nothing is written to storage, so a reload, a new tab and
 *     the next visit all ask the Worker before anything is drawn.
 *   - KEYED BY MEMBERSHIP. Figures are handed back only to the same
 *     membership, so switching institution cannot show the previous one's
 *     numbers, even for a frame.
 *   - CLEARED ON THE WAY OUT. Logging out, and any 401 from the Worker, empty
 *     it, so the next person to use this tab starts with nothing.
 *   - REVALIDATION STILL RUNS. The cache answers BEFORE the Worker, never
 *     INSTEAD of it, so a role change, a removal, a logout in another tab or
 *     an hour's new figures still arrive a moment later.
 */

import type { LiveData } from "~/app/components/LiveTenant";
import type { SessionPayload } from "~/app/lib/session";

let knownSession: SessionPayload | null = null;
let knownTenant: { readonly key: string; readonly data: LiveData } | null = null;

/** One membership. A switch changes it, and the figures are fetched again. */
export function tenantKey(session: SessionPayload): string {
  return `${session.memberId}:${session.orgName}`;
}

export function readSession(): SessionPayload | null {
  return knownSession;
}

export function rememberSession(session: SessionPayload): void {
  knownSession = session;
}

export function readTenant(key: string): LiveData | null {
  return knownTenant && knownTenant.key === key ? knownTenant.data : null;
}

export function rememberTenant(key: string, data: LiveData): void {
  knownTenant = { key, data };
}

/** On logout, and on any 401 the Worker answers. */
export function clearLiveCache(): void {
  knownSession = null;
  knownTenant = null;
}
